import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Tooltip,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  Code as CodeIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { BASE_URL } from "../../../services/configUrls";
import { AppleStyleSpinner } from "./MemCreation/AppleStyleSpinner";

// Default template for Exhibit A
const DEFAULT_EXHIBIT_A_TEMPLATE = `<h1 style="text-align: center; font-size: 16pt; font-weight: bold; text-transform: uppercase; margin-bottom: 20px;">EXHIBIT A<br/>NON-BINDING TERMS</h1>

<h2 style="font-size: 13pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px;">1. Scope and Intent</h2>
<p style="margin: 10px 0; text-align: justify;">EduSkills and {{ institute_name }} agree to collaborate for the implementation of the EduSkills Industry Center of Excellence (CoE) Program at the University/Institution.</p>

<p style="margin: 10px 0; text-align: justify;">This collaboration aims to enhance students' digital and industry-relevant skills by offering world-class, industry-aligned programs designed to meet the evolving needs of the global workforce. The initiative will provide students with practical knowledge, hands-on experience, and exposure to cutting-edge technologies.</p>

<p style="margin: 10px 0; text-align: justify;">Through this program, EduSkills and the Institution intend to bridge the gap between academic learning and industry expectations, thereby improving student employability and career readiness.</p>

<h2 style="font-size: 13pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px;">2. Role of EduSkills</h2>
<p style="margin: 10px 0;">EduSkills will support the program through the following:</p>
<ul style="margin: 10px 0; padding-left: 30px;">
  <li style="margin: 8px 0;">Provide support and services as outlined in EXHIBIT B.</li>
  <li style="margin: 8px 0;">Allow the use of EduSkills and partner logos for approved branding and promotional materials related to the program.</li>
  <li style="margin: 8px 0;">Conduct virtual orientation sessions and/or workshops for students and faculty.</li>
  <li style="margin: 8px 0;">Offer in-person and/or remote technical and operational support during program execution.</li>
  <li style="margin: 8px 0;">Any future add-on activities or additional services beyond the scope mentioned above will be chargeable, based on the nature of services requested and mutual discussion.</li>
  <li style="margin: 8px 0;">Provide access to the EduSkills CoE Management Dashboard to the designated:
    <ul style="margin-top: 5px; padding-left: 20px;">
      <li style="margin: 8px 0;">Central SPOC</li>
      <li style="margin: 8px 0;">Department-level SPOCs (if appointed)</li>
      <li style="margin: 8px 0;">Institutional leadership such as Principal, Director, TPO, or other authorized representatives</li>
    </ul>
    This dashboard will enable the Institution to track program progress, student participation, certifications, and overall activity status on a regular basis.
  </li>
  <li style="margin: 8px 0;">EduSkills will ensure that regular updates and activity status are shared through the dashboard for transparency and effective monitoring.</li>
  <li style="margin: 8px 0;">EduSkills will also provide student dashboard access only to students officially enrolled by the Institution, limited strictly to the number of students specified in EXHIBIT B.</li>
</ul>

<h2 style="font-size: 13pt; font-weight: bold; margin-top: 20px; margin-bottom: 10px;">3. Role of {{ institute_name }}</h2>
<p style="margin: 10px 0;">The Institution will support the program by:</p>
<ul style="margin: 10px 0; padding-left: 30px;">
  <li style="margin: 8px 0;">Creating awareness among students about the EduSkills Industry Center of Excellence Program, including its benefits and career opportunities.</li>
  <li style="margin: 8px 0;">Appointing a Single Point of Contact (SPOC) who will be responsible for coordination, communication, and timely implementation of the program.
    <ul style="margin-top: 5px; padding-left: 20px;">
      <li style="margin: 8px 0;">The Institution shall ensure that the SPOC is a serious, committed, and readily available representative, as the success and smooth execution of the program largely depend on effective coordination.</li>
      <li style="margin: 8px 0;">If the appointed SPOC is unable to respond, dedicate sufficient time, or support the program due to any reason, the Institution shall immediately appoint an alternate SPOC to avoid disruption.</li>
    </ul>
  </li>
  <li style="margin: 8px 0;">The Institution may also appoint department-wise SPOCs, where required, to monitor and execute the program at the departmental level.</li>
  <li style="margin: 8px 0;">The collaboration will be valid for three (3) years from the date of payment and may be renewed after completion of the term. For detailed validity and entitlements, reference should be made to the Membership Certificate issued by EduSkills.</li>
  <li style="margin: 8px 0;">Providing the required infrastructure for conducting program-related activities, if any, including:
    <ul style="margin-top: 5px; padding-left: 20px;">
      <li style="margin: 8px 0;">High-speed internet connectivity</li>
      <li style="margin: 8px 0;">Computer laboratories relevant to the courses</li>
      <li style="margin: 8px 0;">Classrooms with basic IT setup for training sessions and events</li>
    </ul>
  </li>
</ul>

<h2 style="font-size: 13pt; font-weight: bold; margin-top: 30px;">Disclaimer</h2>
<p style="margin: 10px 0; text-align: justify;">This Exhibit outlines non-binding terms and reflects a mutual understanding between EduSkills and the Institution. It is intended solely for academic collaboration and program implementation purposes and does not constitute a legally binding agreement.</p>`;

const ExhibitAModal = ({ open, onClose, content, mouId, onSuccess }) => {
  const [exhibitContent, setExhibitContent] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const quillRef = useRef(null);

  // Tooltip states
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [selectedRange, setSelectedRange] = useState(null);
  const selectionTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Use provided content or fall back to default template
      setExhibitContent(content || DEFAULT_EXHIBIT_A_TEMPLATE);
      setIsEditMode(false);
      setError("");
    }
  }, [open, content]);

  // Rich text editor configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
  ];

  // Handle selection change in editor
  const handleSelectionChange = (range, source, editor) => {
    // Clear existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Hide tooltip immediately on any selection change
    setShowTooltip(false);

    if (range && range.length > 0 && source === "user") {
      const selectedText = editor.getText(range.index, range.length);

      if (
        selectedText &&
        selectedText.trim() &&
        selectedText.trim().length > 0
      ) {
        // Wait 300ms before showing tooltip to avoid interference
        selectionTimeoutRef.current = setTimeout(() => {
          const bounds = quillRef.current.getEditor().getBounds(range.index);
          const editorContainer = quillRef.current.getEditor().container;
          const containerRect = editorContainer.getBoundingClientRect();

          setTooltipPosition({
            top: containerRect.top + bounds.top - 40, // Position above selection
            left: containerRect.left + bounds.left + bounds.width / 2,
          });
          setSelectedRange(range);
          setShowTooltip(true);
        }, 300);
      } else {
        setShowTooltip(false);
        setSelectedRange(null);
      }
    } else {
      setShowTooltip(false);
      setSelectedRange(null);
    }
  };

  const insertInstituteName = () => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, "{{ institute_name }}", "user");
      editor.setSelection(range.index + 18);
    }
    setShowTooltip(false);
  };

  const insertInstituteNameAtSelection = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const editor = quillRef.current?.getEditor();
    if (editor && selectedRange) {
      editor.deleteText(selectedRange.index, selectedRange.length);
      editor.insertText(selectedRange.index, "{{ institute_name }}", "user");
      editor.setSelection(selectedRange.index + 18);
    }

    setShowTooltip(false);
    setSelectedRange(null);
  };

  const handleEditorChange = (content, delta, source, editor) => {
    setExhibitContent(content);
    // Hide tooltip on content change
    if (source === "user") {
      setShowTooltip(false);
    }
  };

  const handleToggleEditMode = () => {
    setIsEditMode(!isEditMode);
    setError("");
    setShowTooltip(false);
    setSelectedRange(null);
  };

  // Convert Quill alignment classes to inline styles for PDF compatibility
  const convertAlignmentToInlineStyles = (html) => {
    // Create a temporary div to parse HTML
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Find all elements with Quill alignment classes
    const alignedElements = temp.querySelectorAll('[class*="ql-align"]');

    alignedElements.forEach((element) => {
      const classList = element.classList;

      if (classList.contains("ql-align-center")) {
        element.style.textAlign = "center";
        classList.remove("ql-align-center");
      } else if (classList.contains("ql-align-right")) {
        element.style.textAlign = "right";
        classList.remove("ql-align-right");
      } else if (classList.contains("ql-align-left")) {
        element.style.textAlign = "left";
        classList.remove("ql-align-left");
      } else if (classList.contains("ql-align-justify")) {
        element.style.textAlign = "justify";
        classList.remove("ql-align-justify");
      }

      // Remove class attribute if empty
      if (element.classList.length === 0) {
        element.removeAttribute("class");
      }
    });

    // Also handle ql-indent classes to preserve indentation
    const indentedElements = temp.querySelectorAll('[class*="ql-indent"]');

    indentedElements.forEach((element) => {
      const classList = element.classList;

      // Extract indent level (e.g., ql-indent-1, ql-indent-2, etc.)
      classList.forEach((className) => {
        if (className.startsWith("ql-indent-")) {
          const indentLevel = className.replace("ql-indent-", "");
          element.style.paddingLeft = `${indentLevel * 30}px`;
        }
      });

      // Remove ql-indent classes
      const indentClasses = Array.from(classList).filter((c) =>
        c.startsWith("ql-indent-")
      );
      indentClasses.forEach((c) => classList.remove(c));

      // Remove class attribute if empty
      if (element.classList.length === 0) {
        element.removeAttribute("class");
      }
    });

    return temp.innerHTML;
  };

  const handleUpdate = async () => {
    // Remove empty paragraphs and trim
    const cleanContent = exhibitContent.replace(/<p><br><\/p>/g, "").trim();

    // Create a temporary div to check if there's actual text content
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = cleanContent;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";

    // Check if there's meaningful content (more than just whitespace)
    if (
      !cleanContent ||
      cleanContent === "<p><br></p>" ||
      textContent.trim().length === 0
    ) {
      setError("Exhibit A content cannot be empty");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Convert alignment classes to inline styles before sending
      const contentWithInlineStyles =
        convertAlignmentToInlineStyles(cleanContent);

      const payload = {
        mou_id: mouId,
        exhibit_a_content: contentWithInlineStyles,
      };

      const response = await axios.post(
        `${BASE_URL}/accounts/update-exhibit-a`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      alert(response?.data?.message || "Exhibit A updated successfully");
      onSuccess?.();
      onClose(); // Close the modal after successful update
    } catch (error) {
      console.error("Error updating Exhibit A:", error);
      setError(
        error.response?.data?.detail ||
          "Failed to update Exhibit A. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setExhibitContent("");
      setIsEditMode(false);
      setError("");
      setShowTooltip(false);
      setSelectedRange(null);
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxWidth: "900px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E5E7EB",
            pb: 2,
            px: 3,
            pt: 2.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {isEditMode ? (
              <EditIcon sx={{ fontSize: 20, color: "#8B5CF6" }} />
            ) : (
              <VisibilityIcon sx={{ fontSize: 20, color: "#3B82F6" }} />
            )}
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1F1F1F",
                letterSpacing: "-0.01em",
              }}
            >
              {isEditMode ? "Edit Exhibit A" : "View Exhibit A"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {!isEditMode && (
              <Button
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                onClick={handleToggleEditMode}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#8B5CF6",
                  "&:hover": {
                    backgroundColor: "#F5F3FF",
                  },
                  px: 1.5,
                  py: 0.5,
                }}
              >
                Edit
              </Button>
            )}
            <IconButton
              onClick={handleClose}
              disabled={loading}
              size="small"
              sx={{
                color: "#9CA3AF",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  color: "#6B7280",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 3, py: 4 }}>
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, fontSize: "13px" }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {isEditMode ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  mt: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: "#1F1F1F",
                  }}
                >
                  Content Editor
                </Typography>
                <Button
                  size="small"
                  startIcon={<CodeIcon sx={{ fontSize: 16 }} />}
                  onClick={insertInstituteName}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#3B82F6",
                    "&:hover": {
                      backgroundColor: "#EFF6FF",
                    },
                    px: 1.5,
                    py: 0.5,
                  }}
                >
                  Insert Institute Name
                </Button>
              </Box>

              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  overflow: "hidden",
                  backgroundColor: "#FFFFFF",
                  position: "relative",
                  "& .ql-container": {
                    fontSize: "14px",
                    fontFamily: "'Inter', -apple-system, sans-serif",
                    minHeight: "400px",
                    maxHeight: "500px",
                    overflowY: "auto",
                  },
                  "& .ql-toolbar": {
                    backgroundColor: "#F9FAFB",
                    borderBottom: "1px solid #E5E7EB",
                    borderTop: "none",
                    borderLeft: "none",
                    borderRight: "none",
                  },
                  "& .ql-editor": {
                    minHeight: "400px",
                    maxHeight: "500px",
                    padding: "16px",
                  },
                  "& .ql-editor.ql-blank::before": {
                    color: "#9CA3AF",
                    fontStyle: "normal",
                    fontSize: "14px",
                  },
                  "& .ql-stroke": {
                    stroke: "#6B7280",
                  },
                  "& .ql-fill": {
                    fill: "#6B7280",
                  },
                  "& .ql-picker-label": {
                    color: "#6B7280",
                  },
                  "& .ql-toolbar button:hover .ql-stroke": {
                    stroke: "#3B82F6",
                  },
                  "& .ql-toolbar button:hover .ql-fill": {
                    fill: "#3B82F6",
                  },
                  "& .ql-toolbar button.ql-active .ql-stroke": {
                    stroke: "#3B82F6",
                  },
                  "& .ql-toolbar button.ql-active .ql-fill": {
                    fill: "#3B82F6",
                  },
                }}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={exhibitContent}
                  onChange={handleEditorChange}
                  onChangeSelection={handleSelectionChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter custom content for Exhibit A or paste HTML directly. Select text to insert template variable."
                  readOnly={loading}
                />
              </Box>

              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#6B7280",
                  mt: 1.5,
                }}
              >
                You can use the toolbar to format text, select any text to
                replace with{" "}
                <code
                  style={{
                    backgroundColor: "#F3F4F6",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "11px",
                  }}
                >
                  {"{{ institute_name }}"}
                </code>
                , or use the "Insert Institute Name" button.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography
                sx={{
                  fontSize: "15px",
                  fontWeight: 600,
                  color: "#1F1F1F",
                  mb: 2,
                  mt: 2,
                }}
              >
                Content Preview
              </Typography>
              <Box
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  p: 3,
                  backgroundColor: "#FFFFFF",
                  minHeight: "400px",
                  maxHeight: "500px",
                  overflowY: "auto",
                  "& h1, & h2, & h3, & h4, & h5, & h6": {
                    marginTop: "16px",
                    marginBottom: "8px",
                    fontWeight: 600,
                    color: "#1F1F1F",
                  },
                  "& h1": {
                    fontSize: "20px",
                    textAlign: "center",
                  },
                  "& h2": {
                    fontSize: "18px",
                  },
                  "& h3": {
                    fontSize: "16px",
                  },
                  "& p": {
                    marginBottom: "12px",
                    lineHeight: "1.6",
                    color: "#4B5563",
                    fontSize: "14px",
                  },
                  "& ul, & ol": {
                    marginBottom: "12px",
                    paddingLeft: "24px",
                  },
                  "& li": {
                    marginBottom: "6px",
                    lineHeight: "1.6",
                    color: "#4B5563",
                    fontSize: "14px",
                  },
                  "& strong": {
                    fontWeight: 600,
                    color: "#1F1F1F",
                  },
                  "& a": {
                    color: "#3B82F6",
                    textDecoration: "underline",
                  },
                }}
                dangerouslySetInnerHTML={{ __html: exhibitContent }}
              />
            </Box>
          )}

          {loading && (
            <Box sx={{ mt: 3 }}>
              <LinearProgress
                sx={{
                  backgroundColor: "#E5E7EB",
                  height: "6px",
                  borderRadius: "3px",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#3B82F6",
                    borderRadius: "3px",
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #E5E7EB",
            px: 3,
            py: 2.5,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              color: "#6B7280",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
            }}
          >
            {isEditMode ? "Cancel" : "Close"}
          </Button>
          {isEditMode && (
            <Button
              variant="contained"
              onClick={handleUpdate}
              disabled={loading}
              sx={{
                backgroundColor: "#3B82F6",
                "&:hover": { backgroundColor: "#2563EB" },
                "&.Mui-disabled": {
                  backgroundColor: "#93C5FD",
                  color: "#FFFFFF",
                },
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                boxShadow: "none",
                minWidth: "120px",
                px: 3,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {loading && <AppleStyleSpinner />}
              {loading ? "Updating..." : "Update"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Floating Tooltip on Selection */}
      {showTooltip && isEditMode && (
        <Fade in={showTooltip}>
          <Box
            onMouseDown={insertInstituteNameAtSelection}
            sx={{
              position: "fixed",
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: "translateX(-50%)",
              zIndex: 9999,
              backgroundColor: "#1F2937",
              color: "#FFFFFF",
              padding: "6px 12px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: 500,
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.2)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              whiteSpace: "nowrap",
              userSelect: "none",
              "&:hover": {
                backgroundColor: "#374151",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: "-4px",
                left: "50%",
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "4px solid transparent",
                borderRight: "4px solid transparent",
                borderTop: "4px solid #1F2937",
              },
            }}
          >
            <CodeIcon sx={{ fontSize: 12 }} />
            {"{{institute_name}}"}
          </Box>
        </Fade>
      )}
    </>
  );
};

export default ExhibitAModal;
